import React, {useEffect, useMemo, useState} from "react";
import {
    Button,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownTrigger,
    getKeyValue,
    Input,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
    useDisclosure,
    User,
    Select,
    SelectItem
} from "@nextui-org/react";
import {BsThreeDotsVertical} from "react-icons/bs";
import {useDispatch, useSelector} from "react-redux";
import {
    changeRole, 
    deleteUser, 
    removeAdminRole,
    setSearch, 
    setSelectedUser, 
    getUsers, 
    setCurrentPage, 
    setPageSize,
    resUser
} from "../../../redux/reducers/UserReducer";
import ModalAssignRoles from "../../modal/ModalAssignRoles";
import {getUserRoles, setUserRoles} from "../../../redux/reducers/GroupReducer";
import PaginationUniv from "../../uiComponents/Pagination";
import {SearchIcon} from "../../icons/SearchIcon";
import {assignRolesToUser} from "../../../redux/reducers/PortalReducer";
import ModalConfirmDelete from "../../modal/ModalConfirmDelete";
import {setIsAssigned} from "../../../redux/reducers/StyleReducer";
import ModalUser from "../../../components/modal/ModalUser";

function UserList() {
    const dispatch = useDispatch()
    const {onOpen, onClose, isOpen} = useDisclosure()
    const {onOpen: onOpenConfirm, onClose: onCloseConfirm, isOpen: isOpenConfirm} = useDisclosure()
    const {
        onOpen: onOpenUserCreate, 
        onClose: onCloseUserCreate, 
        isOpen: isOpenUserCreate
    } = useDisclosure();
    const {
        users,
        selectedUser,
        isLoading,
        search,
        isChangingRole,
        currentPage,
        pageSize,
        totalUsers,
        totalPages,
        availablePageSizes,
        userCreationMessage
    } = useSelector(state => state.userReducer);
    const [searchInput, setSearchInput] = useState(search || "");
    const [showSearchError, setShowSearchError] = useState(false);

    // Debounced search effect - only search if 3+ characters or empty
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (searchInput === "" || searchInput.length >= 3) {
                dispatch(setSearch(searchInput));
                dispatch(getUsers({ page: 1, pageSize, search: searchInput }));
                setShowSearchError(false);
            } else if (searchInput.length > 0 && searchInput.length < 3) {
                setShowSearchError(true);
            }
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [searchInput, dispatch, pageSize]);

    useEffect(() => {
        dispatch(getUsers({ page: currentPage, pageSize, search }));
    }, [dispatch]);

    const loadingState = isLoading ? "loading" : "idle";

    function handleAssign(item) {
        onOpen()
        dispatch(setIsAssigned())
        dispatch(setSelectedUser(item))
    }

    useEffect(() => {
        if (!isOpen) {
            dispatch(setSelectedUser(""))
            dispatch(setUserRoles([]))
        }
    }, [isOpen, dispatch]);

    useEffect(() => {
        dispatch(resUser(""));
    }, [onOpenUserCreate, dispatch]);

    function handleDelete(user) {
        onOpenConfirm()
        dispatch(setSelectedUser(user))
    }

    function handleRoleChange(item) {
        let roleStatus = item?.app_metadata?.user_roles.length ? "ADMIN" : "USER"
        let role_id = "rol_Y4I8wOkJlIFjE8GZ"
        dispatch(setSelectedUser(item.user_id))
        if (roleStatus === "ADMIN") {
            dispatch(removeAdminRole({userId: item.user_id, roleId: role_id}))
        } else {
            dispatch(changeRole({userId: item.user_id, role: {id: role_id, name: "ADMIN"}}))
        }
    }

    function handlePageChange(page) {
        dispatch(setCurrentPage(page));
        dispatch(getUsers({ page, pageSize, search }));
    }

    function handlePageSizeChange(newPageSize) {
        dispatch(setPageSize(parseInt(newPageSize)));
        dispatch(getUsers({ page: 1, pageSize: parseInt(newPageSize), search }));
    }

    function handleSearchInputChange(value) {
        setSearchInput(value);
        if (value === "") {
            setShowSearchError(false);
        }
    }

    function handleSearchClear() {
        setSearchInput("");
        setShowSearchError(false);
        dispatch(setSearch(""));
        dispatch(getUsers({ page: 1, pageSize, search: "" }));
    }

    const topContent = useMemo(() => {
        return (
            <div className="flex flex-col gap-2">
                <div className="flex justify-between gap-3">
                    <div className="w-full">
                        <Input
                            inputMode={"search"}
                            isClearable
                            size={"sm"}
                            className="w-full shadow-xl font-bold"
                            placeholder="Search by name or email (min 3 characters)"
                            startContent={<SearchIcon/>}
                            value={searchInput}
                            onClear={handleSearchClear}
                            isInvalid={showSearchError}
                            errorMessage={showSearchError ? "Search must be at least 3 characters long" : ""}
                            classNames={{
                                input: ["bg-transparent", "text-black/90 dark:text-white/90"],
                            }}
                            onChange={(e) => handleSearchInputChange(e.target.value)}
                        />
                    </div>
                    <div className="flex items-center">
                        <Button 
                            onClick={onOpenUserCreate}
                            className={"bg-secondColor text-white shadow-xl font-bold h-full px-6 py-2"}
                            radius={"sm"}
                        >
                            Add User
                        </Button>
                    </div>
                </div>
            </div>
        );
    }, [searchInput, showSearchError, onOpenUserCreate]);

    const bottomContent = useMemo(() => {
        return totalPages > 0 ? (
            <div className="py-2 px-2 flex justify-between items-center">
                <span className="w-[30%] text-small text-default-400">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalUsers)} of {totalUsers} users
                </span>
                <div className="flex w-full justify-center">
                    <PaginationUniv 
                        setPage={handlePageChange} 
                        page={currentPage} 
                        pages={totalPages}
                    />
                     {/* <div className="flex gap-3">
                        <Select
                            size="sm"
                            label="Rows per page"
                            placeholder={pageSize.toString()}
                            value={pageSize.toString()}
                            onChange={(e) => handlePageSizeChange(e.target.value)}
                            className="max-w-xs"
                        >
                            {availablePageSizes.map((size) => (
                                <SelectItem key={size} value={size}>
                                    {size}
                                </SelectItem>
                            ))}
                        </Select>
                    </div> */}
                </div>
                <div className="hidden sm:flex w-[30%] justify-end gap-2">
                    <Button
                        isDisabled={currentPage === 1}
                        size="sm"
                        variant="flat"
                        onPress={() => handlePageChange(currentPage - 1)}
                    >
                        Previous
                    </Button>
                    <Button
                        isDisabled={currentPage === totalPages}
                        size="sm"
                        variant="flat"
                        onPress={() => handlePageChange(currentPage + 1)}
                    >
                        Next
                    </Button>
                </div>
            </div>
        ) : null;
    }, [currentPage, totalPages, totalUsers, pageSize]);

    return (
        <div>
            <Table
                key={isChangingRole}
                aria-label="Users table with server-side pagination"
                topContent={topContent}
                bottomContent={bottomContent}
                topContentPlacement="outside"
                bottomContentPlacement="outside"
                classNames={{
                    wrapper: "min-h-[222px]",
                }}
            >
                <TableHeader>
                    <TableColumn key="picture">Name</TableColumn>
                    <TableColumn key="email">Email</TableColumn>
                    <TableColumn key="status">Status</TableColumn>
                    <TableColumn key="actions">Actions</TableColumn>
                </TableHeader>
                <TableBody
                    items={users ?? []}
                    loadingContent={<Spinner/>}
                    loadingState={loadingState}
                    emptyContent={search ? "No users found matching your search." : "No users found."}
                >
                    {(item) => (
                        <TableRow key={item?.user_id}>
                            {(columnKey) => {
                                if (columnKey === "picture") {
                                    return (
                                        <TableCell>
                                            <User
                                                avatarProps={{radius: "lg", src: item.picture}} 
                                                name={item.name}
                                            />
                                        </TableCell>
                                    );
                                } else if (columnKey === "status") {
                                    return (
                                        <TableCell>
                                            <p className={"small"}>
                                                {
                                                    !isChangingRole ? (selectedUser === item.user_id ?
                                                            <span className={"bg-yellow-100 text-yellow-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded dark:bg-yellow-900 dark:text-yellow-300"}>
                                                                wait...
                                                            </span> : (item?.app_metadata?.user_roles?.length ?
                                                                    <span className="bg-green-100 text-green-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded dark:bg-green-900 dark:text-green-300">
                                                                        ADMIN
                                                                    </span>
                                                                    :
                                                                    <span className="bg-blue-100 text-blue-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded dark:bg-blue-900 dark:text-blue-300">
                                                                        User
                                                                    </span>
                                                                )) :
                                                        (item?.app_metadata?.user_roles?.length ?
                                                                <span className="bg-green-100 text-green-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded dark:bg-green-900 dark:text-green-300">
                                                                    ADMIN
                                                                </span>
                                                                :
                                                                <span className="bg-blue-100 text-blue-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded dark:bg-blue-900 dark:text-blue-300">
                                                                    User
                                                                </span>
                                                        )
                                                }
                                            </p>
                                        </TableCell>
                                    );
                                } else if (columnKey === "actions") {
                                    return (
                                        <TableCell>
                                            <div className="relative">
                                                <Dropdown className="bg-background border-1 border-default-200">
                                                    <DropdownTrigger>
                                                        <Button isIconOnly radius="full" variant="light">
                                                            <BsThreeDotsVertical size={"24px"}/>
                                                        </Button>
                                                    </DropdownTrigger>
                                                    <DropdownMenu aria-label={"menu"}>
                                                        <DropdownItem onClick={() => handleAssign(item)}>
                                                            Assign Roles
                                                        </DropdownItem>
                                                        <DropdownItem onClick={() => handleDelete(item)}>
                                                            Delete User
                                                        </DropdownItem>
                                                        <DropdownItem onClick={() => handleRoleChange(item)}>
                                                            {item?.app_metadata?.user_roles?.length ? "Make User" : "Make Admin"}
                                                        </DropdownItem>
                                                    </DropdownMenu>
                                                </Dropdown>
                                            </div>
                                        </TableCell>
                                    );
                                } else {
                                    return <TableCell>{getKeyValue(item, columnKey)}</TableCell>;
                                }
                            }}
                        </TableRow>
                    )}
                </TableBody>
            </Table>

            <ModalUser 
                message={userCreationMessage} 
                isOpen={isOpenUserCreate} 
                onClose={onCloseUserCreate}
            />
            <ModalAssignRoles
                id={selectedUser}
                isOpen={isOpen}
                onClose={onClose}
                assignRolesToItem={assignRolesToUser}
                getAssignedRoles={getUserRoles}
                setAssignedRoles={setUserRoles}
                setSelectedItem={setSelectedUser}
                selectedItem={selectedUser.email}
            />
            <ModalConfirmDelete
                id={selectedUser.user_id}
                name={"User"}
                onClose={onCloseConfirm}
                isOpen={isOpenConfirm}
                itemTitle={selectedUser.nickname}
                deleteItem={deleteUser}
            />
        </div>
    );
}

export default UserList;